import { track as vercelTrack } from '@vercel/analytics';

/**
 * Map of analytics event names to their payload shape.
 *
 * Adding a new event:
 *   1. Add a key here with its payload shape (or `undefined` for no payload).
 *   2. Call `track('your_event')` or `track('your_event', { ... })` anywhere.
 *
 * Vercel Web Analytics counts a session as "bounce" when only the initial
 * pageview event fires. Every `track()` call is a second event, so any
 * instrumented interaction breaks the bounce condition for that session.
 * The Pro tier surfaces these events in the dashboard; on Hobby they still
 * count toward the engagement metric but are not browsable.
 */
export interface EventMap {
  splash_dismissed: { via: 'cta' | 'enter' | 'esc' };
  preset_loaded: { presetId: string };
  material_changed: { surface: string; material: string };
  object_placed: { defId: string };
  object_removed: { defId: string };
  step_advanced: { from: number; to: number };
  analysis_started: { duration: number };
  analysis_viewed: undefined;
  about_opened: undefined;
  donation_shown: undefined;
  donation_clicked: { location: 'banner' | 'topbar' | 'mobile_footer' };
  mobile_preset_loaded: { presetId: string };
  mobile_link_copied: undefined;
}

type TrackArgs<K extends keyof EventMap> = EventMap[K] extends undefined
  ? [name: K]
  : [name: K, data: EventMap[K]];

export function track<K extends keyof EventMap>(...args: TrackArgs<K>): void {
  const [name, data] = args as [K, EventMap[K] | undefined];
  if (data === undefined) {
    vercelTrack(name);
  } else {
    vercelTrack(
      name,
      data as unknown as Record<string, string | number | boolean | null>,
    );
  }
}
